import { Suspense, lazy, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import {
  ArrowRight,
  Check,
  Cloud,
  Inbox,
  Mail,
  Plus,
  RotateCw,
  SearchX,
  Settings,
  Trash2,
  User,
} from 'lucide-react';

import { Alert } from '@mds/shared/ui/feedback/alert';
import { Avatar, AvatarFallback, AvatarImage } from '@mds/shared/ui/shadcn/avatar';
import { Badge } from '@mds/shared/ui/shadcn/badge';
import { Button } from '@mds/shared/ui/shadcn/button';
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@mds/shared/ui/shadcn/card';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@mds/shared/ui/shadcn/dialog';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@mds/shared/ui/shadcn/dropdown-menu';
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@mds/shared/ui/shadcn/empty';
import { Field } from '@mds/shared/ui/form/field';
import { Input } from '@mds/shared/ui/shadcn/input';
import { Label } from '@mds/shared/ui/shadcn/label';
import { Separator } from '@mds/shared/ui/shadcn/separator';
import { Skeleton } from '@mds/shared/ui/shadcn/skeleton';
import { notify } from '@mds/shared/ui/shadcn/sonner';
import { Textarea } from '@mds/shared/ui/shadcn/textarea';

/**
 * The seat map stays lazy for the same reason it is kept out of the shared
 * barrel: it pulls a stylesheet and its own artwork, so only the pages that
 * actually show it should pay for it.
 */
const SeatMap = lazy(() => import('@mds/shared/seat-map').then((m) => ({ default: m.SeatMap })));

/** One component's entry: what it is called, where it lives, how to import it. */
function Section({
  id,
  name,
  file,
  importPath,
  summary,
  children,
}: {
  id: string;
  name: string;
  file: string;
  importPath: string;
  summary: string;
  children: ReactNode;
}) {
  return (
    <section id={id} className="scroll-mt-20 border-t pt-10">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <h2 className="text-2xl font-semibold tracking-tight">{name}</h2>
        <span className="font-mono text-xs text-muted-foreground">{file}</span>
      </div>
      <p className="mt-2 max-w-prose text-sm text-muted-foreground">{summary}</p>
      <code className="mt-3 block w-fit rounded-md bg-muted px-2 py-1 font-mono text-xs">
        {importPath}
      </code>
      <div className="mt-6 flex flex-col gap-6">{children}</div>
    </section>
  );
}

/** A single labelled demo inside a section. */
function Demo({ label, note, children }: { label: string; note?: string; children: ReactNode }) {
  return (
    <div>
      <p className="font-mono text-xs tracking-wider text-muted-foreground uppercase">{label}</p>
      {note && <p className="mt-1 text-sm text-muted-foreground">{note}</p>}
      <div className="mt-3 flex flex-wrap items-center gap-3 rounded-lg border p-4">{children}</div>
    </div>
  );
}

const CONTENTS = [
  ['button', 'Button'],
  ['badge', 'Badge'],
  ['alert', 'Alert'],
  ['card', 'Card'],
  ['dialog', 'Dialog'],
  ['dropdown-menu', 'DropdownMenu'],
  ['field', 'Field'],
  ['input', 'Input'],
  ['textarea', 'Textarea'],
  ['label', 'Label'],
  ['avatar', 'Avatar'],
  ['separator', 'Separator'],
  ['skeleton', 'Skeleton'],
  ['empty', 'Empty'],
  ['toast', 'Toast'],
  ['seat-map', 'SeatMap'],
] as const;

export default function ComponentsPage() {
  const [showStatus, setShowStatus] = useState(true);
  const [showArchived, setShowArchived] = useState(false);
  const [sort, setSort] = useState('recent');

  return (
    <div className="mx-auto max-w-page px-gutter py-section">
      <Badge variant="outline">Reference</Badge>
      <h1 className="mt-4 text-4xl font-semibold tracking-tight text-balance">Shared components</h1>
      <p className="mt-3 max-w-prose text-lg text-muted-foreground">
        Every component the apps can import from <code className="font-mono">@mds/shared</code>,
        rendered live with its variants and states. This is the whole set: anything not on this
        page has not been installed yet.
      </p>

      <nav className="mt-8 flex flex-wrap gap-2">
        {CONTENTS.map(([id, name]) => (
          <Button key={id} variant="outline" size="sm" asChild>
            <a href={`#${id}`}>{name}</a>
          </Button>
        ))}
      </nav>

      <div className="mt-12 flex flex-col gap-12">
        <Section
          id="button"
          name="Button"
          file="shared/src/ui/shadcn/button.tsx"
          importPath="import { Button, buttonVariants } from '@mds/shared/ui/shadcn/button';"
          summary="Six variants and four sizes. asChild hands the styling to whatever child you pass, which is how a link becomes a button. An svg child is sized automatically and the padding tightens to match."
        >
          <Demo label="variant">
            <Button>default</Button>
            <Button variant="secondary">secondary</Button>
            <Button variant="destructive">destructive</Button>
            <Button variant="outline">outline</Button>
            <Button variant="ghost">ghost</Button>
            <Button variant="link">link</Button>
          </Demo>
          <Demo label="size" note="icon is square; the others set a height and horizontal padding.">
            <Button size="sm">sm</Button>
            <Button size="default">default</Button>
            <Button size="lg">lg</Button>
            <Button size="icon" aria-label="Settings">
              <Settings />
            </Button>
          </Demo>
          <Demo label="with an icon" note="No size class needed. The child svg gets size-4.">
            <Button>
              <Mail /> Send invite
            </Button>
            <Button variant="outline">
              Continue <ArrowRight />
            </Button>
            <Button variant="destructive">
              <Trash2 /> Delete
            </Button>
          </Demo>
          <Demo label="disabled" note="Pointer events off, 50% opacity.">
            <Button disabled>default</Button>
            <Button variant="outline" disabled>
              outline
            </Button>
          </Demo>
          <Demo label="asChild" note="Renders a react-router Link that looks like a button.">
            <Button asChild>
              <Link to="/">Back home</Link>
            </Button>
            <Button variant="link" asChild>
              <a href="#button">Anchor</a>
            </Button>
          </Demo>
        </Section>

        <Section
          id="badge"
          name="Badge"
          file="shared/src/ui/shadcn/badge.tsx"
          importPath="import { Badge, badgeVariants } from '@mds/shared/ui/shadcn/badge';"
          summary="A span, not a button, so it takes no size prop. One axis, five values: two shapes for a plain label and three status surfaces from lib/tone. shadcn's secondary and destructive are gone rather than sitting alongside neutral and danger as a second way to say the same thing. The [a&] rules mean the hover state only appears when asChild turns it into a link."
        >
          <Demo
            label="variant"
            note="default and outline are labels; success, warning and danger say what state a row is in. Wash, edge and text rather than a solid fill, so a column of them doesn't read as a chart."
          >
            <Badge>default</Badge>
            <Badge variant="outline">outline</Badge>
            <Badge variant="success">success</Badge>
            <Badge variant="warning">warning</Badge>
            <Badge variant="danger">danger</Badge>
          </Demo>
          <Demo label="with an icon" note="A child svg is pinned to size-3.">
            <Badge>
              <Check /> Confirmed
            </Badge>
            <Badge variant="outline">
              <Cloud /> Synced
            </Badge>
          </Demo>
          <Demo label="asChild" note="As a link it gains a hover tint.">
            <Badge asChild>
              <Link to="/">Linked badge</Link>
            </Badge>
          </Demo>
        </Section>

        <Section
          id="alert"
          name="Alert"
          file="shared/src/ui/feedback/alert.tsx"
          importPath="import { Alert } from '@mds/shared/ui/feedback/alert';"
          summary="One component, four tones, props not slots. Every alert in the handoff is the same mark/title/body, so there is nothing to assemble. Sits in the flow next to the thing it describes and never auto-dismisses. One per area: if two conditions are true, show the blocking one. A message about a single field belongs under that field, and a message about work that finished is a toast."
        >
          <Demo
            label="tone"
            note="The two blocking tones announce themselves to a screen reader; the two advisory ones wait their turn."
          >
            <Alert tone="success" title="Reserved · pending payment">
              We've held the Small Meeting Room for Tue 8 Sep, 10:00–12:00. Pay $16 at the front
              desk to confirm.
            </Alert>
            <Alert tone="warning" title="Two-hour minimum applies">
              The Meeting &amp; Training Room bills a minimum of two hours. Your 90-minute slot is
              charged as two.
            </Alert>
            <Alert tone="danger" title="That slot is already booked">
              Someone reserved 15:00–16:00 while you were filling this in. Pick another time or
              space.
            </Alert>
            <Alert tone="neutral" title="Payment is taken in person">
              There is no online checkout. Every reservation is confirmed by email and paid at the
              front desk.
            </Alert>
          </Demo>
          <Demo
            label="action"
            note="Optional, sits at the right and wraps below the copy when there is no room for it."
          >
            <Alert
              tone="danger"
              title="That slot is already booked"
              action={
                <Button variant="outline" size="sm">
                  Pick another
                </Button>
              }
            >
              Someone reserved 15:00–16:00 while you were filling this in.
            </Alert>
            <Alert
              tone="warning"
              title="Deposit still due"
              action={
                <Button variant="outline" size="sm">
                  <RotateCw /> Retry
                </Button>
              }
            >
              $80 is due at the desk before the event.
            </Alert>
            <Alert
              tone="neutral"
              title="Payment is taken in person"
              action={
                <Button variant="link" size="sm" asChild>
                  <Link to="/">How it works</Link>
                </Button>
              }
            />
          </Demo>
          <Demo label="title only" note="The body is optional; the title never is.">
            <Alert tone="success" title="Paid in full, nothing outstanding on this booking." />
          </Demo>
          <Demo
            label="icon={false} / custom icon"
            note="Drop the mark only where something else already carries the meaning."
          >
            <Alert tone="neutral" icon={false} title="No mark">
              Colour and position are doing the work here.
            </Alert>
            <Alert tone="warning" icon={Cloud} title="A different mark">
              Any lucide icon can stand in for the tone's default.
            </Alert>
          </Demo>
        </Section>

        <Section
          id="card"
          name="Card"
          file="shared/src/ui/shadcn/card.tsx"
          importPath="import { Card, CardHeader, CardTitle, CardDescription, CardAction, CardContent, CardFooter } from '@mds/shared/ui/shadcn/card';"
          summary="Seven slots. CardHeader is a grid that switches to two columns on its own when a CardAction is present, so the action lands top-right without any positioning from you. Padding comes from the --card tokens, not from literals."
        >
          <Demo label="full anatomy">
            <Card className="w-full max-w-sm">
              <CardHeader>
                <CardTitle>Desk GA4</CardTitle>
                <CardDescription>Ground floor · window run</CardDescription>
                <CardAction>
                  <Badge variant="outline">Available</Badge>
                </CardAction>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  CardContent carries the body. It gets the same horizontal padding as the header.
                </p>
              </CardContent>
              <CardFooter className="border-t">
                <Button size="sm">Book</Button>
              </CardFooter>
            </Card>
          </Demo>
          <Demo label="header only" note="Every slot is optional.">
            <Card className="w-full max-w-sm">
              <CardHeader>
                <CardTitle>Minimal</CardTitle>
                <CardDescription>Title and description, nothing else.</CardDescription>
              </CardHeader>
            </Card>
          </Demo>
        </Section>

        <Section
          id="dialog"
          name="Dialog"
          file="shared/src/ui/shadcn/dialog.tsx"
          importPath="import { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose } from '@mds/shared/ui/shadcn/dialog';"
          summary="Radix underneath, so focus trapping, Esc, scroll lock and the aria wiring are free. DialogContent is portalled and takes showCloseButton. Leave it on unless the footer already offers a way out."
        >
          <Demo label="default" note="Open it: focus moves in, Esc closes, the page behind locks.">
            <Dialog>
              <DialogTrigger asChild>
                <Button variant="outline">Open dialog</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Release this desk?</DialogTitle>
                  <DialogDescription>
                    The booking is cancelled and the desk goes back on the map straight away.
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                  <DialogClose asChild>
                    <Button variant="outline">Keep it</Button>
                  </DialogClose>
                  <DialogClose asChild>
                    <Button variant="destructive">Release</Button>
                  </DialogClose>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </Demo>
          <Demo
            label="showCloseButton={false}"
            note="Drops the corner X. Only do this when the footer gives a way out."
          >
            <Dialog>
              <DialogTrigger asChild>
                <Button variant="outline">Open without the X</Button>
              </DialogTrigger>
              <DialogContent showCloseButton={false}>
                <DialogHeader>
                  <DialogTitle>No corner close</DialogTitle>
                  <DialogDescription>Esc and the button below still work.</DialogDescription>
                </DialogHeader>
                <DialogFooter>
                  <DialogClose asChild>
                    <Button>Done</Button>
                  </DialogClose>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </Demo>
        </Section>

        <Section
          id="dropdown-menu"
          name="DropdownMenu"
          file="shared/src/ui/shadcn/dropdown-menu.tsx"
          importPath="import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, ... } from '@mds/shared/ui/shadcn/dropdown-menu';"
          summary="Fourteen exports: plain items, checkbox items, a radio group, submenus, labels, separators and a right-aligned shortcut hint. Items support variant=destructive and inset. Arrow keys, typeahead and Esc come from Radix."
        >
          <Demo label="everything at once" note="Open it and try the arrow keys.">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline">
                  <User /> Account
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56">
                <DropdownMenuLabel>Signed in</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuGroup>
                  <DropdownMenuItem>
                    <User /> Profile
                    <DropdownMenuShortcut>⇧⌘P</DropdownMenuShortcut>
                  </DropdownMenuItem>
                  <DropdownMenuItem>
                    <Settings /> Settings
                  </DropdownMenuItem>
                  <DropdownMenuItem disabled>Billing (disabled)</DropdownMenuItem>
                </DropdownMenuGroup>
                <DropdownMenuSeparator />
                <DropdownMenuCheckboxItem checked={showStatus} onCheckedChange={setShowStatus}>
                  Show status
                </DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem checked={showArchived} onCheckedChange={setShowArchived}>
                  Show archived
                </DropdownMenuCheckboxItem>
                <DropdownMenuSeparator />
                <DropdownMenuLabel inset>Sort by</DropdownMenuLabel>
                <DropdownMenuRadioGroup value={sort} onValueChange={setSort}>
                  <DropdownMenuRadioItem value="recent">Most recent</DropdownMenuRadioItem>
                  <DropdownMenuRadioItem value="name">Name</DropdownMenuRadioItem>
                </DropdownMenuRadioGroup>
                <DropdownMenuSeparator />
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger>
                    <Plus /> Invite
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent>
                    <DropdownMenuItem>
                      <Mail /> By email
                    </DropdownMenuItem>
                    <DropdownMenuItem>Copy link</DropdownMenuItem>
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
                <DropdownMenuSeparator />
                <DropdownMenuItem variant="destructive">
                  <Trash2 /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <p className="text-sm text-muted-foreground">
              status: {String(showStatus)} · archived: {String(showArchived)} · sort: {sort}
            </p>
          </Demo>
        </Section>

        <Section
          id="field"
          name="Field"
          file="shared/src/ui/form/field.tsx"
          importPath="import { Field } from '@mds/shared/ui/form/field';"
          summary="The stack around a control, not a replacement for one: label above, hint or error below, and the control gets id, aria-describedby and aria-invalid handed to it. Those three are what go out of step when a form is typed by hand, and when they do a screen reader announces the box and never the message. Two of the handoff's rules are enforced here rather than left to the caller: the error replaces the hint instead of joining it, and an error is either present or it is not. There is no separate invalid flag to fall out of sync with the message."
        >
          <Demo
            label="the four states"
            note="Nothing is passed to the controls. Field wires each one from the label and error it was given."
          >
            <div className="grid w-full gap-4 sm:grid-cols-2">
              <Field label="Full name" hint="Helper text explains the format.">
                <Input placeholder="Full name" />
              </Field>
              <Field
                label="Phone"
                required
                error="Enter a Lebanese number, 8 digits after the prefix."
              >
                <Input type="tel" defaultValue="03 000" />
              </Field>
              <Field label="Email" hint="We only email about your booking.">
                <Input type="email" placeholder="you@example.com" />
              </Field>
              <Field label="Space" hint="Set by the front desk.">
                <Input defaultValue="Small Meeting Room" disabled />
              </Field>
            </div>
          </Demo>
          <Demo
            label="any control"
            note="Anything that accepts the three attributes works, here a Textarea."
          >
            <Field label="Notes" error="Tell us roughly how many people to expect.">
              <Textarea placeholder="Anything we should know?" />
            </Field>
          </Demo>
        </Section>

        <Section
          id="input"
          name="Input"
          file="shared/src/ui/shadcn/input.tsx"
          importPath="import { Input } from '@mds/shared/ui/shadcn/input';"
          summary="A styled native input. Every native prop passes through. The fill is what separates the states: tinted at rest, white when focused, so the field you are typing in is the one that looks like paper. It styles aria-invalid itself, so validation is a matter of setting that attribute rather than adding classes. One departure from the mockup: its invalid border is 2.7:1 against its own fill, under the 3:1 WCAG asks of a non-text indicator, so this keeps the stronger border at 6.5:1 and adds the mockup's wash behind it."
        >
          <Demo label="states">
            <Input className="max-w-xs" placeholder="Placeholder" />
            <Input className="max-w-xs" defaultValue="With a value" />
            <Input className="max-w-xs" placeholder="Disabled" disabled />
          </Demo>
          <Demo label="aria-invalid" note="The only thing that turns an input red. No error class.">
            <Input className="max-w-xs" aria-invalid defaultValue="not-an-email" />
          </Demo>
          <Demo
            label="types"
            note="type is forwarded untouched; file inputs get their own styling."
          >
            <Input className="max-w-xs" type="email" placeholder="you@example.com" />
            <Input className="max-w-xs" type="password" defaultValue="secret" />
            <Input className="max-w-xs" type="date" />
            <Input className="max-w-xs" type="file" />
          </Demo>
        </Section>

        <Section
          id="textarea"
          name="Textarea"
          file="shared/src/ui/shadcn/textarea.tsx"
          importPath="import { Textarea } from '@mds/shared/ui/shadcn/textarea';"
          summary="Uses field-sizing-content, so it grows with what you type instead of scrolling inside a fixed box. min-h-16 is the floor."
        >
          <Demo label="auto-grow" note="Type a few lines and the box follows.">
            <Textarea className="max-w-md" placeholder="Start typing…" />
          </Demo>
          <Demo label="states">
            <Textarea className="max-w-md" defaultValue="Disabled" disabled />
            <Textarea className="max-w-md" aria-invalid defaultValue="Invalid" />
          </Demo>
        </Section>

        <Section
          id="label"
          name="Label"
          file="shared/src/ui/shadcn/label.tsx"
          importPath="import { Label } from '@mds/shared/ui/shadcn/label';"
          summary="Radix Label, so htmlFor focuses the control and clicking the text works. It dims itself when the control it sits beside is a disabled peer, or when an ancestor carries group-data-[disabled=true]."
        >
          <Demo label="with a control">
            <div className="flex w-full max-w-xs flex-col gap-2">
              <Label htmlFor="demo-email">Email</Label>
              <Input id="demo-email" type="email" placeholder="you@example.com" />
            </div>
          </Demo>
          <Demo label="peer-disabled" note="The input is disabled; the label dims on its own.">
            <div className="flex w-full max-w-xs flex-col gap-2">
              <Input id="demo-locked" className="peer" placeholder="Locked" disabled />
              <Label htmlFor="demo-locked">Reads the peer's disabled state</Label>
            </div>
          </Demo>
        </Section>

        <Section
          id="avatar"
          name="Avatar"
          file="shared/src/ui/shadcn/avatar.tsx"
          importPath="import { Avatar, AvatarImage, AvatarFallback } from '@mds/shared/ui/shadcn/avatar';"
          summary="size-8 by default; override with a size class on the root. The fallback is not a placeholder you toggle. Radix swaps to it on its own if the image is missing or fails to load."
        >
          <Demo label="image, fallback, sizes">
            <Avatar>
              <AvatarImage src="/favicon.svg" alt="MDS" />
              <AvatarFallback>MD</AvatarFallback>
            </Avatar>
            <Avatar>
              <AvatarImage src="/does-not-exist.png" alt="Broken" />
              <AvatarFallback>AB</AvatarFallback>
            </Avatar>
            <Avatar>
              <AvatarFallback>
                <User className="size-4" />
              </AvatarFallback>
            </Avatar>
            <Avatar className="size-12">
              <AvatarFallback>LG</AvatarFallback>
            </Avatar>
            <p className="text-sm text-muted-foreground">
              First has an image; second points at a missing file and falls back; third and fourth
              have no image at all.
            </p>
          </Demo>
        </Section>

        <Section
          id="separator"
          name="Separator"
          file="shared/src/ui/shadcn/separator.tsx"
          importPath="import { Separator } from '@mds/shared/ui/shadcn/separator';"
          summary="decorative defaults to true, which hides it from screen readers. Set decorative={false} when the rule genuinely separates two regions. A vertical one needs a height from its parent. accent is how loud the rule is, the same prop name RuledList and IconCard use."
        >
          <Demo label="horizontal">
            <div className="w-full max-w-sm">
              <p className="text-sm">Above</p>
              <Separator className="my-3" />
              <p className="text-sm">Below</p>
            </div>
          </Demo>
          <Demo
            label="accent"
            note="muted is --hairline, the line every ordinary rule uses, and the default. brand is --brand-edge (mint 700), the Tier 2 role for a rule, not the bright mint that would be 1.4:1 here."
          >
            <div className="w-full max-w-sm">
              <p className="text-sm">accent=&quot;muted&quot;</p>
              <Separator className="my-3" />
              <p className="text-sm">accent=&quot;brand&quot;</p>
              <Separator className="my-3" accent="brand" />
            </div>
          </Demo>
          <Demo label="vertical" note="accent applies on both orientations.">
            <div className="flex h-5 items-center gap-3 text-sm">
              <span>Ground</span>
              <Separator orientation="vertical" />
              <span>Upper</span>
              <Separator orientation="vertical" accent="brand" />
              <span>Rooms</span>
            </div>
          </Demo>
        </Section>

        <Section
          id="skeleton"
          name="Skeleton"
          file="shared/src/ui/shadcn/skeleton.tsx"
          importPath="import { Skeleton } from '@mds/shared/ui/shadcn/skeleton';"
          summary="A pulsing block with no size of its own: you give it the shape of whatever it is standing in for. Match the real content's dimensions or the layout jumps when the data lands. It stays invisible for the first 200ms, so a fast render never flashes a loading state; that delay lives in the animation rather than in a timer, so no caller has to remember it."
        >
          <Demo label="shapes">
            <div className="flex w-full max-w-sm items-center gap-4">
              <Skeleton className="size-10 rounded-full" />
              <div className="flex flex-1 flex-col gap-2">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-2/3" />
              </div>
            </div>
          </Demo>
        </Section>

        <Section
          id="empty"
          name="Empty"
          file="shared/src/ui/shadcn/empty.tsx"
          importPath="import { Empty, EmptyHeader, EmptyMedia, EmptyTitle, EmptyDescription, EmptyContent } from '@mds/shared/ui/shadcn/empty';"
          summary="shadcn's empty state, re-toned: the handoff draws a solid hairline where shadcn ships a dashed border, and a warning tone carries the 'couldn't load, try again' block. One component covers all three of the handoff's non-loading list states, because they are the same shape with different words. Every empty state names the next action and every error state offers retry, and that is what EmptyContent is for."
        >
          <Demo label="empty list" note="Says what is missing, then what to do about it.">
            <Empty>
              <EmptyHeader>
                <EmptyMedia variant="icon">
                  <Inbox />
                </EmptyMedia>
                <EmptyTitle>No events this month</EmptyTitle>
                <EmptyDescription>
                  The programme is set a few weeks ahead. Check back, or tell us what you'd like to
                  see.
                </EmptyDescription>
              </EmptyHeader>
              <EmptyContent>
                <Button size="sm">Suggest an event</Button>
              </EmptyContent>
            </Empty>
          </Demo>
          <Demo
            label='tone="warning"'
            note="The retry block. Never blames the user for the network."
          >
            <Empty tone="warning">
              <EmptyHeader>
                <EmptyTitle>Couldn't load the calendar</EmptyTitle>
                <EmptyDescription>
                  The connection dropped. Nothing was lost, so try again.
                </EmptyDescription>
              </EmptyHeader>
              <EmptyContent>
                <Button variant="outline" size="sm">
                  <RotateCw /> Try again
                </Button>
              </EmptyContent>
            </Empty>
          </Demo>
          <Demo label="not found" note="Same component, pointed at the page that does exist.">
            <Empty>
              <EmptyHeader>
                <EmptyMedia variant="icon">
                  <SearchX />
                </EmptyMedia>
                <EmptyTitle>That page has moved</EmptyTitle>
                <EmptyDescription>
                  The link may be old. Everything bookable is on the Spaces page.
                </EmptyDescription>
              </EmptyHeader>
              <EmptyContent>
                <Button variant="link" size="sm" asChild>
                  <Link to="/">Browse spaces</Link>
                </Button>
              </EmptyContent>
            </Empty>
          </Demo>
        </Section>

        <Section
          id="toast"
          name="Toaster / notify"
          file="shared/src/ui/shadcn/sonner.tsx"
          importPath="import { notify } from '@mds/shared/ui/shadcn/sonner';"
          summary="sonner, with its next-themes lookup removed, since the apps are light-only and are not Next. Bottom-right, newest on top, three at a time; pause on hover and on focus come with the library. `notify` applies the handoff's timing rule in one place so no call site has to remember it: success clears itself after four seconds, anything the user must read stays until they dismiss it. Never use a toast for a validation error. That belongs on the field."
        >
          <Demo label="fire one" note="The Toaster is mounted once in the root layout.">
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                notify.success('Registration sent', {
                  description: "We'll email your QR ticket once it's approved.",
                })
              }
            >
              Success · 4s
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                notify.warning('Deposit still due', {
                  description: '$80 is due at the desk before the event.',
                })
              }
            >
              Warning · stays
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                notify.error("Couldn't send that", {
                  description: 'Check your connection and try once more.',
                  action: { label: 'Retry', onClick: () => notify.success('Sent') },
                })
              }
            >
              Error · with action
            </Button>
            <Button variant="ghost" size="sm" onClick={() => notify.dismiss()}>
              Dismiss all
            </Button>
          </Demo>
          <Demo
            label="the stack"
            note="Fire this four times: the oldest drops out rather than the stack growing."
          >
            <Button
              variant="outline"
              size="sm"
              onClick={() => notify.info(`Queued at ${new Date().toLocaleTimeString()}`)}
            >
              Add to the stack
            </Button>
          </Demo>
        </Section>

        <Section
          id="seat-map"
          name="SeatMap"
          file="shared/src/seat-map/"
          importPath="import { SeatMap } from '@mds/shared/seat-map';"
          summary="The one shared component outside ui/. It is kept off the package barrel because it pulls its own stylesheet and lazily-loaded floor artwork, and it themes its own chrome against that artwork with 19 tokens scoped to .seatmap. Controlled or uncontrolled on both space and selected; booked takes a flat list or one per space; onBook may return a promise, and a rejection leaves the desk unconfirmed rather than showing a booking that never happened."
        >
          <Demo
            label="uncontrolled"
            note="Own picker, own selection, own footer. Try booking a desk."
          >
            <Suspense fallback={<Skeleton className="h-96 w-full" />}>
              <SeatMap
                className="w-full"
                booked={['GA1', 'GA2', 'GA7', 'FC3']}
                onBook={(deskId, space) => {
                  console.log('booked', deskId, 'on', space);
                }}
              />
            </Suspense>
          </Demo>
        </Section>

        <section className="scroll-mt-20 border-t pt-10">
          <h2 className="text-2xl font-semibold tracking-tight">Not installed</h2>
          <p className="mt-2 max-w-prose text-sm text-muted-foreground">
            Common shadcn primitives the shared package does not have yet. Add them with the CLI
            into <code className="font-mono">frontend/shared</code> rather than into an app.
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            {[
              'select',
              'checkbox',
              'radio-group',
              'switch',
              'tabs',
              'table',
              'tooltip',
              'popover',
              'sheet',
              'form',
              'calendar',
              'accordion',
              'alert',
              'command',
              'pagination',
              'progress',
              'scroll-area',
              'slider',
              'breadcrumb',
              'collapsible',
            ].map((name) => (
              <Badge key={name} variant="outline">
                {name}
              </Badge>
            ))}
          </div>
          <p className="mt-6 max-w-prose text-sm text-muted-foreground">
            The admin app has its own hand-written set (Button, Card, Modal, Field, Table, Toolbar,
            StatCard, ChipPicker, Chip, EmptyState) under{' '}
            <code className="font-mono">admin/src/components/ui</code>. None of it can be shown
            here: it lives in another workspace, and cross-workspace relative imports are not
            allowed. Promote a component into <code className="font-mono">shared/src/ui</code> to
            make it usable from both apps.
          </p>
        </section>
      </div>
    </div>
  );
}
